import { useRef } from "react";
import { NavLink } from "react-router-dom";
import { useAuthActions } from "@/lib/hooks/useAuthActions";
import type { User } from "@/redux/slices/authSlice";
import { disconnectSocketService } from "@/lib/utils/socket";

// ============================| Types & Interfaces |===========================
interface NavItem {
  id: string;
  title: string;
  to: string;
  icon: React.ReactNode;
  badge?: number;
  highlight?: boolean;
}

const Sidebar = ({
  sidebarOpen,
  isMobile,
  user,
}: {
  sidebarOpen: boolean;
  isMobile: boolean;
  user: User;
}) => {
  // ============================| Hooks |===========================
  const { logout } = useAuthActions();

  // ============================| Refs |===========================
  const navRef = useRef<HTMLElement>(null);

  // ============================| Functions |===========================
  // Navigation items – GENERAL section
  const navItems: NavItem[] = [
    {
      id: "dashboard",
      title: "Home",
      to: "/",
      icon: (
        <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
      ),
    },
  ];

  const teamItems: NavItem[] = [
    {
      id: "live",
      title: "Live",
      to: "/live",
      icon: (
        <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "analytics",
      title: "Analytics",
      to: "/analytics",
      icon: (
        <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: "calendar",
      title: "Calendar",
      to: "/calendar",
      icon: (
        <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "crm",
      title: "CRM",
      to: "/crm",
      icon: (
        <svg className="size-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
  ];

  // Admin only navigation (for customer admins) – MORE section
  const adminNavItems: NavItem[] = [
    // {
    //   id: "reacho-ai",
    //   title: "Reacho AI",
    //   to: "/reacho-ai",
    //   highlight: true,
    //   icon: (
    //     <svg className="size-4 scale-150 shrink-0" viewBox="0 0 24 24" fill="none">
    //       <defs>
    //         <linearGradient id="sidebar-sparkle" x1="0%" y1="0%" x2="100%" y2="100%">
    //           <stop offset="0%" stopColor="#ec4899" />
    //           <stop offset="50%" stopColor="#a855f7" />
    //           <stop offset="100%" stopColor="#3b82f6" />
    //         </linearGradient>
    //       </defs>
    //       <path
    //         fill="url(#sidebar-sparkle)"
    //         d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2z"
    //       />
    //       <path
    //         fill="url(#sidebar-sparkle)"
    //         opacity={0.8}
    //         d="M19 14l.75 2.25L22 17l-2.25.75L19 20l-.75-2.25L16 17l2.25-.75L19 14z"
    //       />
    //     </svg>
    //   ),
    // },
    {
      id: "insights",
      title: "Insights",
      to: "/insights",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    {
      id: "team",
      title: "Team",
      to: "/team",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      id: "widget",
      title: "Widget",
      to: "/widget",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
          />
        </svg>
      ),
    },
    {
      id: "billing",
      title: "Billing",
      to: "/billing",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      ),
    },
  ];

  // SUPER_ADMIN navigation (for Reacho team)
  const superAdminNavItems: NavItem[] = [
    {
      id: "companies",
      title: "Companies",
      to: "/admin/companies",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      id: "platform-analytics",
      title: "Platform Analytics",
      to: "/admin/platform-analytics",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: "support",
      title: "Support",
      to: "/admin/support",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
          />
        </svg>
      ),
    },
    {
      id: "billing-oversight",
      title: "Billing Oversight",
      to: "/admin/billing-oversight",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      ),
    },
    {
      id: "discounts",
      title: "Discount Codes",
      to: "/admin/discounts",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    {
      id: "referrals",
      title: "Referral Codes",
      to: "/admin/referrals",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
      ),
    },
    {
      id: "trials",
      title: "Trials",
      to: "/admin/trials",
      icon: (
        <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  const handleLogout = async () => {
    // Disconnect socket before logging out
    disconnectSocketService();
    // Use the proper logout function that clears Redux state and calls API
    await logout();
  };

  const linkBase =
    "flex items-center gap-3 px-2.5 py-1.5 rounded-lg transition-all w-full text-left text-xs leading-0.5";
  const linkActive =
    "bg-black/10 text-text dark:bg-[#2c2b30] dark:text-[#e1deed] [&_svg]:text-text font-semibold";
  const linkInactive = "text-text hover:dark:bg-white/5 hover:bg-black/5 [&_svg]:text-text";

  return (
    <aside
      className={`transition-transform duration-300 rounded-2xl overflow-hidden bg-layout w-(--sidebar-width)
        ${sidebarOpen || !isMobile ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="flex flex-col h-full py-4">
        {/* Navigation */}
        <nav ref={navRef} className="flex-1 pb-4 sm-scroll">
          {/* GENERAL */}
          <div className="mb-2">
            <div className="px-4 py-1.5 text-[11px] font-semibold text-gray-txt uppercase tracking-wider">
              General
            </div>
            <div className="space-y-1 px-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }: { isActive: boolean }) =>
                    `${linkBase} ${isActive ? linkActive : linkInactive}`
                  }
                >
                  {item.icon}
                  <span>{item.title}</span>
                  {item.badge != null && (
                    <span className="ml-auto bg-[#976BE6] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Team (placeholder) */}
          <div className="mb-2">
            <div className="px-4 py-1.5 text-[11px] font-semibold text-gray-txt uppercase tracking-wider">
              Team
            </div>
            <div className="space-y-1 px-4">
              {teamItems.map((item) => (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }: { isActive: boolean }) =>
                    `${linkBase} ${isActive ? linkActive : linkInactive}`
                  }
                >
                  {item.icon}
                  <span>{item.title}</span>
                  {item.badge != null && (
                    <span className="ml-auto bg-[#976BE6] text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          </div>

          {/* MORE – Admin (customer admins) */}
          {user?.role === "ADMIN" && (
            <div className="mb-2">
              <div className="px-4 py-1.5 text-[11px] font-semibold text-gray-txt uppercase tracking-wider">
                Admin
              </div>
              <div className="space-y-1 px-4">
                {adminNavItems.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    end
                    className={({ isActive }: { isActive: boolean }) =>
                      `${linkBase} ${isActive ? linkActive : linkInactive}`
                    }
                  >
                    {item.icon}
                    <span
                      className={
                        item.highlight
                          ? "bg-linear-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent font-medium"
                          : undefined
                      }
                    >
                      {item.title}
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          {/* MORE – Platform Admin (Reacho team) */}
          {user?.role === "SUPER_ADMIN" && (
            <div className="mb-2">
              <div className="px-4 py-1.5 text-[11px] font-semibold text-gray-txt uppercase tracking-wider">
                Super Admin
              </div>
              <div className="space-y-1 px-4">
                {superAdminNavItems.map((item) => (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    end
                    className={({ isActive }: { isActive: boolean }) =>
                      `${linkBase} ${isActive ? linkActive : linkInactive}`
                    }
                  >
                    {item.icon}
                    <span>{item.title}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </nav>

        {/* Footer: Logout */}
        <div className="px-4 pt-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-2.5 py-1.5 rounded-lg text-xs text-text hover:text-red-400 hover:bg-white/5 transition-colors"
          >
            <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
